import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import logo from '../assets/hamilton-services-logo-notext.png';
import { FiGlobe } from 'react-icons/fi';
import { useLanguage } from '../components';
import './Login.css';

const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const PROD_API_BASE = 'https://hamilton-services-backend.onrender.com/api';
const REQUEST_TIMEOUT = IS_PRODUCTION ? 90000 : 10000;
const normalizeApiBase = (value) => String(value || '').trim().replace(/\/+$/, '');
const isLocalApiBase = (value) => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(String(value || '').trim());

const getPasswordStrength = (pw, tt) => {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 10) score++;
  if (pw.length >= 14) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  if (score <= 2) return { score, label: tt('Weak'), color: '#ef4444' };
  if (score <= 4) return { score, label: tt('Fair'), color: '#f59e0b' };
  if (score === 5) return { score, label: tt('Good'), color: '#22d3ee' };
  return { score, label: tt('Strong'), color: '#10b981' };
};

const resolveApiBase = (apiBase) => {
  const normalizedPropBase = normalizeApiBase(apiBase);
  if (normalizedPropBase) {
    return normalizedPropBase;
  }

  const envBase = normalizeApiBase(process.env.REACT_APP_API_URL);
  if (IS_PRODUCTION) {
    if (envBase && !isLocalApiBase(envBase)) {
      return envBase;
    }
    return PROD_API_BASE;
  }

  return envBase || '/api';
};

const Login = ({ apiBase, fullScreen = false }) => {
  const { lang, setLang, tt } = useLanguage();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [infoLink, setInfoLink] = useState(null);
  const [resetToken, setResetToken] = useState('');
  const emailInputRef = useRef(null);
  const nextLanguage = lang === 'en' ? 'fr' : 'en';
  const switchLanguageLabel = lang === 'en' ? tt('Switch to French') : tt('Switch to English');
  const languageToggleClass = fullScreen ? 'auth-lang-toggle floating' : 'auth-lang-toggle inline';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get('verify_token');
    const resetTokenFromQuery = params.get('reset_token');

    const run = async () => {
      if (verifyToken) {
        try {
          const baseUrl = resolveApiBase(apiBase);
          await axios.post(`${baseUrl}/auth/verify-email/confirm`, { token: verifyToken }, { withCredentials: true, timeout: REQUEST_TIMEOUT });
          setInfoMessage(tt('Email verified successfully. You can now sign in.'));
        } catch (err) {
          setError(err?.response?.data?.message || err?.response?.data?.error || err?.response?.data?.detail || tt('Email verification failed.'));
        }
      }

      if (resetTokenFromQuery) {
        setResetToken(resetTokenFromQuery);
        setMode('reset');
        setInfoMessage(tt('Enter your new password to complete reset.'));
      }
    };

    run();
  }, [apiBase, tt]);

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);
    setInfoLink(null);

    if (mode === 'forgot') {
      if (!email) {
        setError(tt('Email is required.'));
        return;
      }
    } else if (mode === 'reset') {
      if (!password) {
        setError(tt('New password is required.'));
        return;
      }
      if (password !== confirmPassword) {
        setError(tt('Passwords do not match.'));
        return;
      }
      if (!resetToken) {
        setError(tt('Reset token is missing. Please use the link from your email.'));
        return;
      }
    } else if (!email || !password) {
      setError(tt('Email and password are required.'));
      return;
    }

    if (mode === 'signup' && password !== confirmPassword) {
      setError(tt('Passwords do not match.'));
      return;
    }

    setLoading(true);
    try {
      const baseUrl = resolveApiBase(apiBase);
      if (mode === 'forgot') {
        const response = await axios.post(`${baseUrl}/auth/password-reset/request`, { email }, { timeout: REQUEST_TIMEOUT, withCredentials: true });
        const debugLink = response?.data?.debug_link;
        setInfoMessage(response?.data?.message || tt('If your account exists, a password reset link has been sent.'));
        if (debugLink) {
          setInfoLink(debugLink);
        }
      } else if (mode === 'reset') {
        await axios.post(
          `${baseUrl}/auth/password-reset/confirm`,
          { token: resetToken, new_password: password },
          { timeout: REQUEST_TIMEOUT, withCredentials: true }
        );
        setInfoMessage(tt('Password reset successful. You can now sign in.'));
        setMode('login');
        setPassword('');
        setConfirmPassword('');
      } else {
        const endpoint = mode === 'signup' ? `${baseUrl}/auth/register` : `${baseUrl}/auth/login`;
        const response = await axios.post(endpoint, { email, password }, { timeout: REQUEST_TIMEOUT, withCredentials: true });
        const { user } = response.data || {};
        if (!user) {
          throw new Error('Invalid response from server.');
        }

        localStorage.setItem('authUser', JSON.stringify(user));
        localStorage.removeItem('guestMode');
        if (mode === 'signup') {
          setInfoMessage(tt('Account created. Please verify your email if required by your environment.'));
        }
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Auth error:', err);
      
      let errorMessage = tt('Authentication failed.');
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error || err?.response?.data?.detail;
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = IS_PRODUCTION
          ? tt('The server is waking up from sleep — this can take up to 60 seconds on the free tier. Please wait a moment and try again.')
          : tt('Request timeout. Is the backend running on port 8000?');
      } else if (err.message === 'Network Error') {
        errorMessage = IS_PRODUCTION
          ? tt('Unable to reach the server. Please check your connection and try again.')
          : tt('Network error. Make sure the backend is running (run start_backend.bat)');
      } else if (err.response?.status === 400) {
        errorMessage = apiMessage || tt('Invalid email or password.');
      } else if (err.response?.status === 401) {
        errorMessage = tt('Invalid email or password.');
      } else if (err.response?.status === 403) {
        errorMessage = apiMessage || tt('Access denied.');
      } else if (apiMessage) {
        errorMessage = apiMessage;
      } else if (err.response?.status === 502 || err.response?.status === 503) {
        errorMessage = IS_PRODUCTION
          ? tt('The service is temporarily unavailable. Please try again in a few moments.')
          : tt('Backend service unavailable. Make sure the backend is running.');
      }
      
      setError(tt(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={fullScreen ? 'auth-shell' : 'page-container'}>
      <div className={fullScreen ? 'auth-screen' : 'auth-screen auth-screen-embedded'}>
        <button
          type="button"
          className={languageToggleClass}
          onClick={() => setLang(nextLanguage)}
          title={switchLanguageLabel}
          aria-label={switchLanguageLabel}
        >
          <FiGlobe aria-hidden="true" />
          <span>{tt('Language')}</span>
          <strong>{nextLanguage.toUpperCase()}</strong>
        </button>

        {fullScreen && (
          <div className="auth-visual">
            <div className="auth-visual-inner">
              <img src={logo} alt={tt('Hamilton Services')} className="auth-logo" />
              <h2 className="auth-brand-title">{tt('Hamilton Services')}</h2>
              <p className="auth-tagline">
                {tt('Build, optimize, and track portfolios with a pro-grade investing stack.')}
              </p>
            </div>
          </div>
        )}

        <div className="auth-card">
          {fullScreen && (
            <div className="auth-card-head">
              <p className="auth-card-overline">{tt('Secure access')}</p>
              <h1 className="auth-card-title">{tt('Welcome to Hamilton Services')}</h1>
              <p className="auth-card-subtitle">{tt('Sign in to continue, or create an account to save your work.')}</p>
            </div>
          )}

          {!fullScreen && (
            <div className="page-header">
              <h1>{tt('Account')}</h1>
              <p className="page-subtitle">{tt('Sign in or create an account to save portfolios.')}</p>
            </div>
          )}

          <div className="auth-tabs">
            <button
              className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
              onClick={() => setMode('login')}
              type="button"
            >
              {tt('Login')}
            </button>
            <button
              className={`auth-tab ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => setMode('signup')}
              type="button"
            >
              {tt('Create Account')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="login-email">{tt('Email')}</label>
              <input
                id="login-email"
                ref={emailInputRef}
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={tt('you@example.com')}
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            {mode !== 'forgot' && (
              <div className="form-group">
                <label htmlFor="login-password">{mode === 'reset' ? tt('New Password') : tt('Password')}</label>
                <input
                  id="login-password"
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
                {(mode === 'signup' || mode === 'reset') && password && (() => {
                  const strength = getPasswordStrength(password, tt);
                  return (
                    <div className="auth-password-strength">
                      <div className="auth-strength-meter">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                          <div
                            key={i}
                            className="auth-strength-segment"
                            style={{ background: i <= strength.score ? strength.color : 'rgba(148, 163, 184, 0.32)' }}
                          />
                        ))}
                      </div>
                      <span className="auth-strength-label" style={{ color: strength.color }}>{strength.label}</span>
                    </div>
                  );
                })()}
                {mode === 'login' && (
                  <div className="auth-link-row">
                    <button
                      type="button"
                      className="auth-link"
                      onClick={() => {
                        setMode('forgot');
                        setError(null);
                        setInfoMessage(null);
                      }}
                    >
                      {tt('Forgot password?')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {(mode === 'signup' || mode === 'reset') && (
              <div className="form-group">
                <label>{tt('Confirm Password')}</label>
                <input
                  type="password"
                  className="form-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            )}

            {infoMessage && (
              <div className="success-message">
                {infoMessage}
                {infoLink && (
                  <div className="auth-inline-link-row">
                    <a href={infoLink} className="auth-inline-link">
                      {tt('Open reset link')}
                    </a>
                  </div>
                )}
              </div>
            )}
            {error && <div className="error-message" id="login-error" role="alert">{error}</div>}

            <div className="auth-actions">
              <button className="btn-primary" type="submit" disabled={loading}>
                {loading ? tt('Please wait...') : mode === 'signup' ? tt('Create Account') : mode === 'forgot' ? tt('Send Reset Link') : mode === 'reset' ? tt('Set New Password') : tt('Sign In')}
              </button>
              {fullScreen && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    localStorage.setItem('guestMode', 'true');
                    window.location.href = '/';
                  }}
                >
                  {tt('Continue without logging in')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
